
import React, { useEffect } from 'react';
import { useRSVP } from '../../context/RSVPContext';
import { usePhoneVerification } from '../../hooks/usePhoneVerification';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PhoneNameMismatchDialog } from './verification/PhoneNameMismatchDialog';
import { PhoneEntryForm } from './verification/PhoneEntryForm';
import { CodeEntryForm } from './verification/CodeEntryForm';
import { NoGuestSelected } from './verification/NoGuestSelected';
import { Button } from '@/components/ui/button';
import { RSVPButton } from '@/components/ui/rsvp-button';

export const VerificationStep = () => {
  const { 
    selectedGuest, 
    phoneNumber: contextPhoneNumber, 
    setPhoneNumber: setContextPhoneNumber,
    goToNextStep,
    goToPreviousStep,
    eventData
  } = useRSVP();

  const {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    handlePhoneSubmit,
    handleCodeSubmit,
    handleResendCode,
    resetVerification,
    cooldownTime,
    showNameMismatchDialog,
    setShowNameMismatchDialog,
    existingUserName,
    handleContinueWithNewName,
    handleChangePhone,
    isPhoneEntryStep,
    isCodeEntryStep,
    isProcessingPhone,
    isProcessingCode,
    verificationState
  } = usePhoneVerification({
    inviteId: selectedGuest?.inviteId,
    guestName: selectedGuest?.displayName || '',
    onSuccess: () => {
      setContextPhoneNumber(phoneNumber);
      goToNextStep();
    }
  });

  useEffect(() => {
    if (contextPhoneNumber && !phoneNumber) {
      setPhoneNumber(contextPhoneNumber);
    }
  }, [contextPhoneNumber, phoneNumber, setPhoneNumber]);

  const handleSubmitPhone = () => {
    setContextPhoneNumber(phoneNumber);
    handlePhoneSubmit();
  };

  if (!selectedGuest) {
    return <NoGuestSelected onBack={goToPreviousStep} />;
  }

  return (
    <div className="flex flex-col h-full justify-between">
      {/* Main content area with improved vertical spacing */}
      <div className="flex-1 flex flex-col overflow-auto pb-24">
        {verificationState.error && (
          <Alert variant="destructive" className="mx-4 mb-6">
            <AlertDescription>{verificationState.error}</AlertDescription>
          </Alert>
        )}

        {/* Content container with 30% vertical spacing from top */}
        <div className="flex-1 flex flex-col">
          <div className="h-[30vh] min-h-[120px] max-h-[200px]"></div>
          
          <div className="px-4 md:px-6">
            <div className="max-w-md mx-auto w-full text-center">
              <h2 className="text-2xl font-semibold mb-2">
                Hi {selectedGuest.displayName}!
              </h2>

              {isPhoneEntryStep() && (
                <>
                  <p className="text-muted-foreground mb-8">
                    Input your phone number to verify your invite
                  </p>
                  <PhoneEntryForm 
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    onSubmit={handleSubmitPhone}
                    isProcessing={isProcessingPhone()}
                    onBack={goToPreviousStep}
                    error={verificationState.error}
                  />
                </>
              )}

              {isCodeEntryStep() && (
                <>
                  <p className="text-muted-foreground mb-8">
                    Enter the verification code sent to your phone
                  </p>
                  <CodeEntryForm 
                    verificationCode={verificationCode}
                    setVerificationCode={setVerificationCode}
                    onSubmit={handleCodeSubmit}
                    onResendCode={handleResendCode}
                    onChangePhone={resetVerification}
                    isProcessing={isProcessingCode()}
                    cooldownTime={cooldownTime}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer with RSVP button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-12 bg-white">
        <div className="max-w-2xl mx-auto px-4">
          {isPhoneEntryStep() && (
            <>
              <RSVPButton
                onClick={handleSubmitPhone}
                disabled={isProcessingPhone()}
                className="mx-auto block"
              >
                Next
              </RSVPButton>
              
              <Button 
                variant="ghost" 
                className="w-full text-muted-foreground mt-4" 
                onClick={goToPreviousStep}
              >
                Change guest
              </Button>
            </>
          )}

          {isCodeEntryStep() && (
            <>
              <RSVPButton
                onClick={handleCodeSubmit}
                disabled={isProcessingCode() || verificationCode.length !== 6}
                className="mx-auto block"
              >
                Next
              </RSVPButton>
              
              <Button 
                variant="ghost" 
                className="w-full text-muted-foreground mt-4" 
                onClick={resetVerification}
              >
                Change phone number
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Name mismatch dialog */}
      <PhoneNameMismatchDialog 
        isOpen={showNameMismatchDialog} 
        onClose={() => setShowNameMismatchDialog(false)} 
        existingName={existingUserName} 
        newName={selectedGuest.displayName} 
        onContinueWithNew={handleContinueWithNewName} 
        onChangePhone={handleChangePhone} 
      />
    </div>
  );
};
